import React, { useEffect, useMemo } from "react";
import { Pressable, StyleSheet, View, ViewStyle } from "react-native";
import { useTheme } from "../hooks/use-theme";
import Animated, {
    useAnimatedStyle,
    useSharedValue,
    withTiming,
    withSpring,
} from "react-native-reanimated";

export type CarouselStepsProps = {
    count: number;
    index: number;
    stepColor?: string;
    style?: ViewStyle;
    testID?: string;
    onPressIndex?: (index: number) => void;
    barWidth?: number;
};

type CarouselStepProps = {
    current: boolean;
    onPress?: () => void;
    barWidth: number;
};

const CarouselStep: React.FC<CarouselStepProps> = ({ current, onPress, barWidth }) => {
    const fadeColor = "rgba(63,52,0,0.16)";
    const { roundness, colors } = useTheme();
    const styles = useCarouselStepsStyles();
    const widthFactor = useSharedValue(1.0);
    const colorShared = useSharedValue(fadeColor);
    const animatedStyles = useAnimatedStyle(() => {
        return {
            width: widthFactor.value * roundness,
            backgroundColor: colorShared.value,
        };
    });
    useEffect(() => {
        if (current) {
            widthFactor.value = withSpring(barWidth);
            colorShared.value = withTiming(colors.secondary);
        } else {
            widthFactor.value = withSpring(1.0);
            colorShared.value = withTiming(fadeColor);
        }
    }, [current, widthFactor, colorShared, colors, barWidth]);
    return (
        <Pressable onPress={onPress}>
            <Animated.View
                style={[styles.step, current ? styles.currentStep : null, animatedStyles]}
            />
        </Pressable>
    );
};
const CarouselSteps: React.FC<CarouselStepsProps> = ({
    count,
    index,
    style,
    testID,
    onPressIndex,
    barWidth = 3,
}) => {
    const styles = useCarouselStepsStyles();
    return (
        <View style={[styles.view, style]} testID={testID}>
            {[...Array(count).keys()].map((idx) => {
                return (
                    <CarouselStep
                        key={idx}
                        current={index === idx}
                        onPress={onPressIndex ? () => onPressIndex(idx) : undefined}
                        barWidth={barWidth}
                    />
                );
            })}
        </View>
    );
};

const useCarouselStepsStyles = () => {
    const { colors, roundness } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                view: {
                    height: roundness,
                    flexDirection: "row",

                    alignItems: "center",
                    justifyContent: "space-between",
                },
                step: {
                    height: roundness,
                    minHeight: roundness,
                    flex: 0,
                    borderRadius: roundness,
                    marginRight: roundness,
                    backgroundColor: colors.backdrop,
                },
                currentStep: {
                    backgroundColor: colors.secondary,
                },
            }),
        [colors, roundness]
    );
};

export default CarouselSteps;
